import { motion } from 'framer-motion'
import { CheckCircle2, Circle, MoreHorizontal } from 'lucide-react'
import { useAppData } from '../context/AppDataContext'
import { useToast } from '../context/ToastContext'
import { DONE_STATUS, type Task } from '../data/types'
import { daysUntil, formatDate } from '../lib/format'
import { cn } from '../lib/cn'
import { Avatar } from './Avatar'
import { PriorityBadge } from './PriorityBadge'

interface TaskCardProps {
  task: Task
  onOpen: () => void
  dragHandleProps?: Record<string, unknown>
  pulse?: boolean
  onPulseEnd?: () => void
}

export function TaskCard({ task, onOpen, pulse, onPulseEnd }: TaskCardProps) {
  const { setTaskStatus, clients, projects, directory } = useAppData()
  const { showToast } = useToast()
  const client = clients.find((c) => c.id === task.clientId)
  const project = projects.find((p) => p.id === task.projectId)
  const assignee = directory.find((m) => m.id === task.assigneeId)
  const due = daysUntil(task.dueDate)
  const overdue = due < 0 && task.status !== DONE_STATUS
  const isDone = task.status === DONE_STATUS

  const accent = client?.color ?? '#91919c'

  return (
    <motion.div
      layout
      layoutId={task.id}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 28, mass: 0.9 }}
      whileHover={{ y: -3, scale: 1.015, transition: { type: 'spring', stiffness: 420, damping: 22, mass: 0.7 } }}
      whileTap={{ scale: 0.98 }}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      onAnimationEnd={() => pulse && onPulseEnd?.()}
      className={cn(
        'group cursor-pointer rounded-[20px] border p-4 shadow-card transition-shadow hover:shadow-card-hover',
        isDone && 'opacity-70',
        pulse && 'pulse-highlight',
      )}
      style={{
        backgroundColor: `color-mix(in srgb, ${accent} 16%, var(--color-canvas-surface) 84%)`,
        borderColor: `color-mix(in srgb, ${accent} 32%, var(--color-canvas-surface) 68%)`,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold text-white"
            style={{ backgroundColor: accent }}
          >
            {client?.initials}
          </span>
          <span className="truncate text-xs font-semibold text-canvas-fg/80">{client?.name}</span>
        </div>
        <button
          aria-label="Toggle complete"
          onClick={(e) => {
            e.stopPropagation()
            const was = task.status
            setTaskStatus(task.id, isDone ? 'Editing' : DONE_STATUS)
            showToast(isDone ? 'Back to editing.' : `"${task.title}" is done.`, 'success', {
              undo: () => setTaskStatus(task.id, was),
            })
          }}
          className="shrink-0 text-canvas-fg/30 transition-colors hover:text-success-500"
        >
          {isDone ? <CheckCircle2 size={18} className="text-success-500" /> : <Circle size={18} />}
        </button>
      </div>

      <p className={cn('mt-3 text-sm font-semibold leading-snug', isDone ? 'text-canvas-fg/40 line-through' : 'text-canvas-fg')}>
        {task.title}
      </p>
      <p className="mt-0.5 truncate text-xs text-canvas-fg/60">{project?.name}</p>

      {task.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {task.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-canvas-fg/10 px-2 py-0.5 text-[11px] font-medium text-canvas-fg/80">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3.5 flex items-center justify-between border-t pt-3" style={{ borderColor: `${accent}25` }}>
        <div className="flex items-center gap-2">
          <PriorityBadge priority={task.priority} />
          <span className={cn('text-xs font-medium', overdue ? 'text-danger-500' : 'text-canvas-fg/60')}>
            {formatDate(task.dueDate)}
          </span>
        </div>
        {assignee && <Avatar name={assignee.name} color={assignee.color} size="xs" />}
      </div>
    </motion.div>
  )
}

export function TaskCardMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-md p-1 text-canvas-fg/40 hover:bg-canvas-fg/10 hover:text-canvas-fg">
      <MoreHorizontal size={15} />
    </button>
  )
}
